/**
 * Auth store (Zustand).
 *
 * Tracks the current user and whether the session has been checked. The store
 * is a singleton in the browser; `getAuthStore()` lets non-React code (the
 * fetch mutator) reach it to clear state on 401.
 */

import { createStore, type StoreApi } from 'zustand/vanilla';
import { useStore } from 'zustand';

import type { User } from '@/lib/types';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  hasCheckedSession: boolean;
  setUser: (user: User | null) => void;
  setCheckedSession: (checked: boolean) => void;
  logout: () => void;
}

function createAuthStore(initialUser: User | null = null): StoreApi<AuthState> {
  return createStore<AuthState>((set) => ({
    user: initialUser,
    isAuthenticated: initialUser !== null,
    hasCheckedSession: initialUser !== null,
    setUser: (user) =>
      set({ user, isAuthenticated: user !== null, hasCheckedSession: true }),
    setCheckedSession: (checked) => set({ hasCheckedSession: checked }),
    logout: () => set({ user: null, isAuthenticated: false, hasCheckedSession: true }),
  }));
}

let store: StoreApi<AuthState> | null = null;

export function getAuthStore(initialUser: User | null = null): StoreApi<AuthState> {
  if (!store) {
    store = createAuthStore(initialUser);
  }
  return store;
}

/** React hook selector over the singleton auth store. */
export function useAuthStore<T>(selector: (state: AuthState) => T): T {
  return useStore(getAuthStore(), selector);
}
